/**
 * Audit/debug log for reconcilePolicyTags runs.
 *
 * Why: PPPC and App Config deployment hinges on the policy-tag reconciliation
 * step. When "the agent never got the profile", we need to be able to look
 * back and see exactly which run intended to deploy, what add/remove deltas
 * it computed, which endpoints it hit, with which payloads, and what the
 * backend returned. Toasts and React Query errors are too lossy for that.
 *
 * Storage: localStorage (capped ring buffer). Matches the existing PPPC
 * activity log pattern — no backend dependency, survives reloads, easy to
 * dump in a bug report.
 *
 * Surfaces: read APIs (`getReconcileLog`, `getReconcileLogForPolicy`) for
 * future UI panels; `clearReconcileLog` for tests / "report a bug" reset.
 */
const STORAGE_KEY = "tagReconcileDebugLog/v1";
const MAX_ENTRIES = 200;
export const RECONCILE_LOG_UPDATED_EVENT = "tag-reconcile-log-updated";

export type ReconcileCallOutcome =
  | "success"
  | "duplicate-ignored"
  | "error"
  | "skipped";

export interface ReconcileCall {
  /** HTTP method the apiClient invoked. */
  method: "POST" | "DELETE";
  /** Full backend path. */
  path: string;
  /** Payload sent to the backend (what reconcile constructed). */
  payload: Record<string, unknown>;
  outcome: ReconcileCallOutcome;
  /** Backend error message when outcome === "error". */
  errorMessage?: string;
  /** Backend error code when available (e.g. ApiError.errorCode). */
  errorCode?: string;
  /** Wall-clock duration of the call in ms (best-effort). */
  durationMs?: number;
}

export interface ReconcileLogEntry {
  id: string;
  /** ISO timestamp when the reconcile run started. */
  at: string;
  /** Free-form caller hint, e.g. "useSavePppcPolicy" or "AppConfigWizard". */
  source?: string;
  policyId: string;
  actor: string;
  previousTagIds: string[];
  nextTagIds: string[];
  /** Computed deltas. Empty arrays mean "no add/remove needed". */
  added: string[];
  removed: string[];
  /** Per-API-call breakdown so a failed deploy can be traced end-to-end. */
  calls: ReconcileCall[];
  /** Aggregate outcome of the whole run. */
  outcome: "success" | "partial" | "error" | "noop";
  /** Total wall-clock duration of the reconcile in ms. */
  totalDurationMs: number;
}

function safeReadStorage(): ReconcileLogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ReconcileLogEntry[]) : [];
  } catch {
    return [];
  }
}

function safeWriteStorage(entries: ReconcileLogEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(0, MAX_ENTRIES)),
    );
    window.dispatchEvent(new CustomEvent(RECONCILE_LOG_UPDATED_EVENT));
  } catch {
    // Quota exceeded / privacy mode — silently drop.
  }
}

function genId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try {
      return (crypto as Crypto).randomUUID();
    } catch {
      /* fall through */
    }
  }
  return `rl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Push a new run entry to the log. Newest-first ordering. */
export function recordReconcileRun(entry: Omit<ReconcileLogEntry, "id" | "at">): ReconcileLogEntry {
  const full: ReconcileLogEntry = { id: genId(), at: new Date().toISOString(), ...entry };
  const existing = safeReadStorage();
  safeWriteStorage([full, ...existing]);
  return full;
}

/** Read the full log, newest-first. */
export function getReconcileLog(): ReconcileLogEntry[] {
  return safeReadStorage();
}

/** Read entries for a single policy id, newest-first. */
export function getReconcileLogForPolicy(policyId: string): ReconcileLogEntry[] {
  return safeReadStorage().filter((e) => e.policyId === policyId);
}

/** Clear the log. Intended for tests or an explicit "reset diagnostics" UI. */
export function clearReconcileLog(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(RECONCILE_LOG_UPDATED_EVENT));
  } catch {
    /* noop */
  }
}
